import { useBillingSummaryQuery } from "@/hooks/useBillingSummaryQuery";
import { isApiErrorStatus } from "@/api/ApiError";
import { formatMinutesAsHMM, formatOrdinalDate, humanizeDuration } from "@/lib/utils";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Heading from "../Heading/Heading";
import type { PeriodSummary } from "@/types/Billing";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnOrganisationOwner } from "@/core/auth";
import { useOrganisationBillingSummaryQuery } from "@/hooks/useOrganisationBillingSummaryQuery";
import { flattenOrgBilling } from "@/lib/flattenOrgBilling";

/** "0900" → "09:00" */
const formatHHmm = (t: string): string => (t && t.length >= 4 ? `${t.slice(0, 2)}:${t.slice(2, 4)}` : t);

export interface PeriodTableProps {
  caption: string;
  period: PeriodSummary;
  emptyMessage: string;
  showCompany?: boolean;
}

export const PeriodTable = ({ caption, period, emptyMessage, showCompany = false }: PeriodTableProps) => {
  return (
    <div className="mb-2">
      <p className="text-sm text-gray-600 px-2 pt-1 pb-2">{caption}</p>
      {period.rows.length === 0 &&
      period.totals.cancelledFlexMinutes === 0 &&
      period.totals.cancelledWeekendMinutes === 0 &&
      period.totals.gratisMinutes === 0 ? (
        <p className="text-sm text-gray-500 px-2 pb-3">{emptyMessage}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Room</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Ref</TableHead>
              <TableHead className="text-right">Hours consumed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {period.rows.map((row, i) => (
              <TableRow key={`${row.ref}-${row.date}-${i}`}>
                <TableCell>
                  <span className="block">{formatOrdinalDate(row.date)}</span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    {formatHHmm(row.startTime)}–{formatHHmm(row.endTime)}{" "}
                    <span className="hidden md:inline">({humanizeDuration(row.startTime, row.endTime)})</span>
                  </span>
                </TableCell>
                <TableCell>{row.locationName}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${row.roomSwatch}`} />
                    <span className="hidden md:block">{row.roomName}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {row.gratis ? (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-gray-200 text-xs font-semibold text-gray-600">No Charge</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
                      {row.type === "slot" ? "Slot" : "Flexible"}
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs uppercase text-gray-700">{row.ref?.toUpperCase() || "—"}</span>
                  {showCompany && (row as { company?: boolean; memberName?: string }).company && (
                    <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-indigo-100 text-[10px] font-semibold text-indigo-800">
                      Company{(row as { memberName?: string }).memberName ? ` · ${(row as { memberName?: string }).memberName}` : ""}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatMinutesAsHMM(row.durationMinutes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={5} className="font-medium">
                Total Flex
              </TableCell>
              <TableCell className="text-right tabular-nums font-medium">
                {formatMinutesAsHMM(period.totals.flexMinutes)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} className="font-medium">
                Total Slot
              </TableCell>
              <TableCell className="text-right tabular-nums font-medium">
                {formatMinutesAsHMM(period.totals.slotMinutes)}
              </TableCell>
            </TableRow>
            {period.totals.weekendFlexMinutes > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="font-medium">
                  Total Weekend Flex
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMinutesAsHMM(period.totals.weekendFlexMinutes)}
                </TableCell>
              </TableRow>
            )}
            {period.totals.weekendSlotMinutes > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="font-medium">
                  Total Weekend Slot
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMinutesAsHMM(period.totals.weekendSlotMinutes)}
                </TableCell>
              </TableRow>
            )}
            {period.totals.cancelledFlexMinutes > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="font-medium text-amber-700">
                  Flex Cancellations
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium text-amber-700">
                  {formatMinutesAsHMM(period.totals.cancelledFlexMinutes)}
                </TableCell>
              </TableRow>
            )}
            {period.totals.cancelledWeekendMinutes > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="font-medium text-amber-700">
                  Weekend Cancellations
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium text-amber-700">
                  {formatMinutesAsHMM(period.totals.cancelledWeekendMinutes)}
                </TableCell>
              </TableRow>
            )}
            {period.totals.gratisMinutes > 0 && (
              <TableRow>
                <TableCell colSpan={5} className="font-medium text-gray-500">
                  No Charge
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium text-gray-500">
                  {formatMinutesAsHMM(period.totals.gratisMinutes)}
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
      )}
    </div>
  );
};

const BillingSummary = () => {
  const { member } = useMember();
  const isOwner = isMemberAnOrganisationOwner(member);
  const orgId = member?.ownerOfOrganisationId ?? member?.organisation?.id;
  const personal = useBillingSummaryQuery();
  const org = useOrganisationBillingSummaryQuery(isOwner ? orgId : null);

  const active = isOwner ? org : personal;
  const isDeployGap = active.isError && isApiErrorStatus(active.error, 404);
  const flat = isOwner && org.data ? flattenOrgBilling(org.data, member!.id) : null;

  return (
    <div className="mt-8">
      <div className="mb-4">
        <Heading title={isOwner ? "Your & company billing" : "Your billing"} />
      </div>
      <div className="bg-gray-100 border-2 border-gray-100 rounded-lg p-2 border-solid" data-testid="billing-summary">
        {active.isLoading && <p className="text-sm text-gray-600 p-4 text-center">Loading your billing summary…</p>}
        {isDeployGap && (
          <p className="text-sm text-gray-600 p-4 text-center">
            Your billing summary isn't available right now — an update may be in progress. Please try again in a few minutes.
          </p>
        )}
        {active.isError && !isDeployGap && (
          <p className="text-sm text-red-600 p-4 text-center">Couldn't load your billing summary. Please try again later.</p>
        )}
        {isOwner && flat && (
          <>
            <PeriodTable caption="Last Month" period={flat.lastMonth} emptyMessage="No bookings last month." showCompany />
            <PeriodTable caption="This Month (so far)" period={flat.thisMonth} emptyMessage="No completed bookings this month yet." showCompany />
          </>
        )}
        {!isOwner && personal.data && (
          <>
            <PeriodTable caption="Last Month" period={personal.data.lastMonth} emptyMessage="No bookings last month." />
            <PeriodTable
              caption="This Month (so far)"
              period={personal.data.thisMonth}
              emptyMessage="No completed bookings this month yet."
            />
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSummary;
